import { InfoIcon } from '@mm/icons';
import { map, noop } from 'lodash';
import pluralize from 'pluralize';
import React, { ReactNode } from 'react';
import SelectorWithArrow from '../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  FollowUpKind,
  FollowUpModel,
  FollowUpStateType,
  kindByEntityType,
  QqFollowUpModel,
} from '@mm/state/model/FollowUpModel';
import { AccordionItem } from '@mm/components';
import './FollowUps.pcss';
import { GenericFollowUps } from './GenericFollowUps/GenericFollowUps';

const items = [{ id: 'send_reminder', name: 'Send Reminder' }];

type ItemProps<T> = {
  records: T[];
  kind: FollowUpKind;
  headingNote: string;
  landmineName: keyof typeof landmineNames;
  hideWidgetAdder: boolean;
  thenSelector: React.ReactNode;
  showTimeWarning?: boolean;
  bottomNote?: React.ReactNode;
};

const resolveInterval = <T extends FollowUpModel<QqFollowUpModel>>(
  records: T[],
) => {
  if (records[0]) {
    const { interval } = records[0];
    const delimiterType = interval % 3600 === 0 ? 'hour' : 'minute';
    const delimiterValue =
      delimiterType === 'hour' ? interval / 3600 : interval / 60;
    return `${delimiterValue} ${pluralize(delimiterType, delimiterValue)}`;
  }
  return 'XX minutes/hours';
};

const followUpsByKind: Record<
  FollowUpKind,
  {
    header: string;
    subHeader: <T extends FollowUpModel<QqFollowUpModel>>(
      records: T[],
    ) => string;
    headingNote: string;
    landmineName: keyof typeof landmineNames;
    classNamePrefix: string;
    hideWidgetAdder: boolean;
    thenSelector: React.ReactNode;
    showTimeWarning?: boolean;
    bottomNote?: React.ReactNode;
    component: <T extends FollowUpModel<QqFollowUpModel>>(
      props: ItemProps<T>,
    ) => ReactNode;
  }
> = {
  qq_follow_up: {
    header: 'Customer did not respond to a Quick Question',
    subHeader: records => `Send reminder after ${resolveInterval(records)}`,
    classNamePrefix: 'qq-follow-ups',
    component: GenericFollowUps,
    headingNote: 'If customer has not responded to a Quick Question after',
    landmineName: 'QQ_FOLLOW_UP_ACTIVATION_CHECKBOX',
    hideWidgetAdder: false,
    bottomNote: (
      <div className="generic-follow-up-item__info-message">
        <InfoIcon className="generic-follow-up-item__info-icon" />
        <span>After the reminder bot will re-send the Quick Question</span>
      </div>
    ),
    thenSelector: (
      <>
        <span className="generic-follow-up-item__then-label">Then</span>
        <SelectorWithArrow
          selectedItem={items[0]}
          className="generic-follow-up-item__then-selector"
          onChange={noop}
          disabled={false}
          items={items}
        />
      </>
    ),
  },
  pending_follow_up: {
    header: 'Conversation has been moved to Pending',
    subHeader: records => `Send follow up after ${resolveInterval(records)}`,
    classNamePrefix: 'pending-follow-ups',
    component: GenericFollowUps,
    headingNote: 'If conversation has been marked Pending after',
    landmineName: 'PENDING_CONVERSATION_FOLLOW_UP_ACTIVATION_CHECKBOX',
    hideWidgetAdder: true,
    showTimeWarning: false,
    thenSelector: (
      <span className="generic-follow-up-item__then-label">
        Then follow up with:
      </span>
    ),
  },
  resolved_follow_up: {
    header: 'Conversation has been moved to Resolved',
    subHeader: records => `Send follow up after ${resolveInterval(records)}`,
    classNamePrefix: 'resolved-follow-ups',
    component: GenericFollowUps,
    headingNote: 'If conversation has been marked Resolved after',
    landmineName: 'RESOLVED_CONVERSATION_FOLLOW_UP_ACTIVATION_CHECKBOX',
    hideWidgetAdder: false,
    thenSelector: (
      <span className="generic-follow-up-item__then-label">
        Then follow up with:
      </span>
    ),
  },
};

type Props = {
  activeKind?: FollowUpKind;
  setActiveKind: (kind?: FollowUpKind) => void;
  followUps: Partial<
    Record<FollowUpStateType, FollowUpModel<QqFollowUpModel>[]>
  >;
  className?: string;
};

export const FollowUpsAccordionItems = (props: Props) => {
  const { activeKind, setActiveKind, followUps } = props;

  return (
    <>
      {map(
        followUps,
        (
          records: FollowUpModel<QqFollowUpModel>[],
          entityType: FollowUpStateType,
        ) => {
          const kind = kindByEntityType[entityType];
          const config = followUpsByKind[kind];
          return (
            <AccordionItem
              key={entityType}
              openOnMount={kind === activeKind}
              arrowPosition="left"
              closeOnOutsideClick={false}
              title={config.header}
              description={config.subHeader(records)}
              descriptionClassName="follow-up__accordion-item-description"
              className={`follow-up__accordion-item ${config.classNamePrefix}__accordion-item`}
              headingClassName={`follow-up__accordion-item-header ${config.classNamePrefix}__accordion-item-header`}
              arrowClassName={`follow-up__accordion-item-arrow ${config.classNamePrefix}__accordion-item-arrow`}
              onToggle={value => setActiveKind(value ? kind : undefined)}
            >
              <div
                className={`follow-up-records ${config.classNamePrefix}__records`}
              >
                {config.component({
                  records,
                  kind,
                  thenSelector: config.thenSelector,
                  showTimeWarning: config.showTimeWarning,
                  hideWidgetAdder: config.hideWidgetAdder,
                  headingNote: config.headingNote,
                  landmineName: config.landmineName,
                  bottomNote: config.bottomNote,
                })}
              </div>
            </AccordionItem>
          );
        },
      )}
    </>
  );
};
